import { Info } from "@phosphor-icons/react";

/**
 * LegalDisclaimer — Reusable "not legal advice" component for Formation Toolkit modules.
 * Signals to founders that FlashGrants guidance is educational, not legal representation.
 *
 * Usage:
 *   <LegalDisclaimer variant="banner" />  → top-of-page prominent notice
 *   <LegalDisclaimer variant="footer" />  → bottom-of-page subtle notice (default)
 *   <LegalDisclaimer variant="inline" />  → mid-content callout at legal decision points
 */
export const LegalDisclaimer = ({ variant = "footer", className = "" }) => {
  if (variant === "banner") {
    return (
      <div
        className={`rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 mb-8 ${className}`}
        data-testid="legal-disclaimer-banner"
      >
        <div className="flex items-start gap-3">
          <Info weight="fill" className="shrink-0 w-5 h-5 text-amber-400 mt-0.5" />
          <div className="text-sm text-amber-100/90 leading-relaxed">
            <b className="text-amber-300">Educational guidance, not legal advice.</b>{" "}
            This toolkit helps you understand and prepare your filings, but does
            not replace a licensed South Carolina attorney. For decisions with
            legal or tax consequences, consult qualified counsel before filing.
          </div>
        </div>
      </div>
    );
  }

  if (variant === "inline") {
    return (
      <div
        className={`rounded border-l-2 border-amber-500/50 bg-zinc-900/50 pl-3 py-2 my-3 ${className}`}
        data-testid="legal-disclaimer-inline"
      >
        <p className="text-xs text-amber-200/80 leading-relaxed">
          <b className="text-amber-300">Consider consulting a SC attorney at this step.</b>{" "}
          This decision has legal consequences beyond the scope of this guidance.
        </p>
      </div>
    );
  }

  // footer variant (default)
  return (
    <div
      className={`mt-10 pt-6 border-t border-zinc-900 ${className}`}
      data-testid="legal-disclaimer-footer"
    >
      <div className="flex items-start gap-3">
        <Info weight="fill" className="shrink-0 w-4 h-4 text-zinc-500 mt-0.5" />
        <p className="text-xs text-zinc-500 leading-relaxed max-w-3xl">
          <b>Educational guidance, not legal advice.</b>{" "}
          This module helps you understand and prepare your filings. It does not
          replace a licensed South Carolina attorney. For decisions with legal
          or tax consequences, consult qualified counsel before filing. FlashGrants
          does not provide legal representation.
        </p>
      </div>
    </div>
  );
};

export default LegalDisclaimer;
